import type { Appointment } from '@dnx/contracts';
import { Alert, Badge, Button, Dialog, Field, Input } from '@dnx/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { BellRing, CalendarClock, Check, LogIn, MessageCircle, UserX, X } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { BookDialog } from './book-dialog.js';
import { hhmm, KIND_LABEL, STATUS_STYLE, TZ } from './queries.js';

const dt = (iso: string) => new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: TZ }).format(new Date(iso));

export function AppointmentDrawer({ appt, onClose, canWrite }: { appt: Appointment | null; onClose: () => void; canWrite: boolean }) {
  const qc = useQueryClient();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const act = useMutation({
    mutationFn: (b: { status: string; cancelReason?: string }) =>
      api<Appointment>(`/scheduling/appointments/${appt!.id}/status`, { method: 'POST', body: { ...b, version: appt!.version } }),
    onSuccess: () => { setCancelling(false); setReason(''); void qc.invalidateQueries({ queryKey: ['scheduling'] }); onClose(); },
  });
  if (!appt) return <Dialog open={false} onClose={onClose} title="">{null}</Dialog>;
  const st = STATUS_STYLE[appt.status]!;
  const future = new Date(appt.startsAt).getTime() > Date.now();
  const open = ['scheduled', 'confirmed'].includes(appt.status);
  const localDay = (iso: string) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date(iso));
  const isToday = localDay(appt.startsAt) === localDay(new Date().toISOString());

  return (
    <>
      <Dialog open={!!appt && !rescheduling} onClose={onClose} variant="drawer" title={appt.contact?.name ?? 'Bloqueio'}
        description={`${KIND_LABEL[appt.kind]} · ${appt.professional?.name ?? ''}`}>
        <div className="space-y-5 p-5 text-sm">
          <div className="rounded-lg border border-border bg-surface-muted p-4">
            <p className="font-semibold text-fg first-letter:uppercase">{dt(appt.startsAt)}</p>
            <p className="mt-0.5 text-fg-muted tabular-nums">{hhmm(appt.startsAt)} – {hhmm(appt.endsAt)}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <Badge tone={appt.status === 'confirmed' ? 'success' : appt.status === 'no_show' ? 'danger' : 'primary'}>{st.label}</Badge>
              {appt.confirmation === 'reschedule_requested' && <Badge tone="warning">Paciente pediu para remarcar</Badge>}
            </div>
          </div>
          <dl className="space-y-2 text-[13px]">
            <div className="flex justify-between"><dt className="text-fg-muted">Celular</dt><dd className="text-fg">{appt.contact?.maskedPhone ?? '—'}</dd></div>
            <div className="flex items-center justify-between"><dt className="flex items-center gap-1.5 text-fg-muted"><BellRing className="size-3.5" />Lembretes</dt>
              <dd className="text-fg">{[appt.reminders.h48 && '48 h', appt.reminders.h24 && '24 h'].filter(Boolean).join(' e ') || 'ainda não enviados'}</dd></div>
            {appt.cancelReason && <div className="flex justify-between"><dt className="text-fg-muted">Motivo</dt><dd className="text-fg">{appt.cancelReason}</dd></div>}
          </dl>
          {appt.conversationId && (
            <Link to="/conversas" search={{ filtro: 'open', c: appt.conversationId }}>
              <Button variant="secondary" size="sm"><MessageCircle className="size-4" />Abrir conversa</Button>
            </Link>
          )}

          {canWrite && appt.status !== 'cancelled' && appt.status !== 'completed' && appt.status !== 'no_show' && (
            <section className="space-y-2 border-t border-border pt-5">
              <h3 className="text-[13px] font-semibold text-fg">Ações</h3>
              <div className="flex flex-wrap gap-2">
                {appt.status === 'scheduled' && <Button size="sm" variant="secondary" onClick={() => act.mutate({ status: 'confirmed' })}><Check className="size-4" />Confirmar presença</Button>}
                {open && isToday && <Button size="sm" onClick={() => act.mutate({ status: 'checked_in' })}><LogIn className="size-4" />Paciente chegou</Button>}
                {appt.status === 'checked_in' && <Button size="sm" onClick={() => act.mutate({ status: 'completed' })}><Check className="size-4" />Concluir</Button>}
                {open && future && <Button size="sm" variant="secondary" onClick={() => setRescheduling(true)}><CalendarClock className="size-4" />Remarcar</Button>}
                {open && !future && <Button size="sm" variant="secondary" onClick={() => act.mutate({ status: 'no_show' })}><UserX className="size-4" />Faltou</Button>}
                {!cancelling && <Button size="sm" variant="ghost" onClick={() => setCancelling(true)}><X className="size-4" />Cancelar</Button>}
              </div>
              {cancelling && (
                <form className="space-y-2 rounded-md border border-border bg-surface-muted p-3" onSubmit={(e) => { e.preventDefault(); act.mutate({ status: 'cancelled', cancelReason: reason }); }}>
                  <Field label="Motivo do cancelamento" hint="O paciente recebe um aviso no WhatsApp."><Input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setCancelling(false)}>Voltar</Button>
                    <Button size="sm" variant="danger" type="submit" disabled={reason.trim().length < 2} loading={act.isPending}>Cancelar consulta</Button>
                  </div>
                </form>
              )}
              {act.error && <Alert tone="warning">{act.error.message}</Alert>}
            </section>
          )}
        </div>
      </Dialog>
      {rescheduling && appt.contact && (
        <BookDialog open onClose={() => { setRescheduling(false); onClose(); }} contactId={appt.contact.id} contactName={appt.contact.name} reschedule={appt} />
      )}
    </>
  );
}
